import WhatsappIcon from '@mui/icons-material/WhatsApp'
import { BoxProps, styled } from '@mui/material'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import { find, findIndex, has, path, pathOr, propEq, reduce, replace } from 'ramda'
import React from 'react'

import { cropText, displayMap as defaultDisplayMap, MESSAGE_TYPES, Q_ACTIONS } from '@/app/module/campaigns/definitions'
import { DisplayMap, LocalAction, Part } from '@/app/module/campaigns/types'
import { getPartContentPreview } from '@/app/module/campaigns/utils/part-content'
import { apiPz } from '@/app/module/logs/store/selectors'
import { addCurlyBrackets } from '@/app/module/utils/personalization'
import { FilesType, PersonalizationType } from '@/app/types'

type WhatsAppPreviewProps = {
  message: {
    text?: string
    image?: string
    audio?: string
    video?: string
    document?: string
  }
  files: FilesType
}

type UpdateContactText = {
  field: string
  value: string
  constant: string
}

type UpdateContactPreviewProps = {
  text: UpdateContactText
  personalizationList: PersonalizationType
}

type SubscribeToCampaignPreviewProps = {
  campaignId: number
  campaignName?: string
}

type TopupPreviewProps = {
  maxAmount: string | number
  maxAmountTemplated?: string
  desiredAmountTemplated?: string
}

type PreviewMapProps = {
  text: string | UpdateContactText | SubscribeToCampaignPreviewProps | TopupPreviewProps
  parts?: Part[]
  personalizationList?: PersonalizationType
  files?: FilesType
}

type Props = {
  action?: LocalAction
  disabled?: boolean
  displayMap?: DisplayMap
  parts?: Part[]
  personalizationList: PersonalizationType
  files?: FilesType
  onEdit?: () => void
}

const ActionPreview: React.FC<Props> = ({
  action,
  disabled,
  displayMap = defaultDisplayMap,
  files = {},
  parts = [],
  personalizationList = {},
  onEdit,
}) => {
  const displaySpecs = action?.type && displayMap[action.type]
  if (!displaySpecs) {
    return null
  }

  let text = ''
  if (action.type === 'sendEmail') {
    text = ''
    if (action[action.type]?.toMultiple?.length) {
      if (action[action.type].toMultiple.length > 1) {
        text = `${action[action.type].toMultiple.length} recipients`
      } else {
        text = `${action[action.type].toMultiple[0]}`
      }
    } else {
      text = pathOr('', displaySpecs.path, action)
    }
  } else if (action.type === 'api') {
    text = displaySpecs.url?.path ? pathOr('', displaySpecs.url.path, action) : ''
  } else if (action.type === 'voice') {
    text = pathOr('', ['play', 'say'], action) || pathOr('', ['play', 'playfile'], action)
  } else if (displaySpecs.isNumber) {
    text = Number(pathOr('', displaySpecs.path, action)).toFixed(2)
  } else text = pathOr('', displaySpecs.path, action)

  const isTTS = action.type === 'voice' && !!pathOr('', ['play', 'say'], action)

  return (
    <Container disabled={disabled} onClick={disabled ? undefined : onEdit}>
      {displaySpecs.icon === 'whatsapp' ? (
        <WhatsappIcon />
      ) : (
        <Icon
          color={disabled ? 'disabled' : 'action'}
          style={{
            width: '30px',
            textAlign: 'center',
            marginRight: '2px',
          }}
        >
          {!isTTS ? displaySpecs.icon : 'text_fields'}
        </Icon>
      )}
      <Typography
        className="campaign-preview-text"
        variant="subtitle1"
        color={disabled ? 'textDisabled' : 'textSecondary'}
      >
        {cropText({
          text: previewMap[action.type]({ text, parts, personalizationList, files }),
          limit: 50,
          firstCutOff: 25,
          secondCutOff: 20,
        })}
      </Typography>
    </Container>
  )
}

const getPersonalizationLabel = (label: string, { contact = [], customfields = [] }: PersonalizationType): string =>
  pathOr('', ['label'], find(propEq('value', label), contact)) ||
  pathOr('', ['label'], find(propEq('value', label), customfields))

const whatsappPreview = ({ message: { text, image, audio, video, document }, files }: WhatsAppPreviewProps): string => {
  let preview = ''
  if (image) {
    preview += `[Image: ${image}] `
  }
  if (audio) {
    preview += `[Voice Note - ${path([audio, 'title'], files) || audio.trim()}]`
  }
  if (video) {
    preview += `[Video: ${video}]`
  }
  if (document) {
    preview += `[Document: ${document}]`
  }
  if (text) {
    preview += text.trim()
  }
  return preview
}

const selectContent = (partIndex: number, parts: Part[]): string =>
  reduce(
    (accum, type) => {
      const message = parts[partIndex]
      if (!message || !has(type.key, message)) {
        return accum
      }

      if (message.label) {
        return `${partIndex + 1}. ${message.label}`
      }
      const text = getPartContentPreview(message)
      return `${partIndex + 1}. ${type.label}: ${text}`
    },
    '',
    MESSAGE_TYPES,
  )

const jumpPreview = ({ text: partId, parts }: PreviewMapProps): string =>
  selectContent(findIndex(propEq('id', partId), parts || []), parts || [])

const updateContactValues: Record<string, string> = {
  response: 'the exact response',
  message: 'the message received',
  answer: 'the answer',
  transcript: 'the transcript',
  ...reduce((acc, { label, value }) => ({ ...acc, [addCurlyBrackets(value)]: label }), {}, apiPz),
}

const updateContactPreview = ({ text, personalizationList }: UpdateContactPreviewProps): string => {
  const { field, value, constant } = text as UpdateContactText
  return `Set "${getPersonalizationLabel(field, personalizationList)}" to ${
    value === 'constant' ? `"${constant}"` : `${updateContactValues[value]}`
  }`
}

const subscribeToCampaignPreview = ({ campaignId, campaignName }: SubscribeToCampaignPreviewProps): string =>
  campaignName || `Campaign ${campaignId}`

const topupPreview = (
  { maxAmount, maxAmountTemplated, desiredAmountTemplated }: TopupPreviewProps,
  personalizationList?: PersonalizationType,
): string => {
  if (maxAmountTemplated && personalizationList) {
    return getPersonalizationLabel(replace(/{|}/g, '', maxAmountTemplated), personalizationList)
  }
  if (Number(desiredAmountTemplated) > 0) {
    return `${Number(desiredAmountTemplated).toFixed(2)}, or up to ${Number(maxAmount).toFixed(2)}`
  }
  return `${Number(maxAmount).toFixed(2)} (max)`
}

const previewMap: Record<string, (props: PreviewMapProps) => string> = {
  runQuota: () => Q_ACTIONS.runQuota.label,
  sms: ({ text }) => (text ? (text as string).trim() : 'No text found for default language.'),
  sendSMSToPhoneNumbers: ({ text }) => (text ? (text as string).trim() : 'No text found for default language.'),
  api: ({ text }) => (text as string).trim(),
  voice: ({ text, files }) =>
    path([text as string, 'title'], files) || (text as string)?.trim() || 'No audio file for default language',
  jump: jumpPreview,
  reopen: ({ text }) => `Campaign ${text}`,
  topup: ({ text, personalizationList }) => topupPreview(text as TopupPreviewProps, personalizationList),
  updateContact: ({ text, personalizationList }) =>
    updateContactPreview({ text: text as UpdateContactText, personalizationList: personalizationList! }),
  sendEmail: ({ text }) => `Send Email to ${(text as string).trim()}`,
  transfer: ({ text }) => `Transfer Call to ${text}`,
  subscribeToCampaign: ({ text }) => subscribeToCampaignPreview(text as SubscribeToCampaignPreviewProps),
  sendWhatsapp: ({ text: message, files }) =>
    whatsappPreview({ message: message as WhatsAppPreviewProps['message'], files: files! }),
}

const Container = styled('div')<BoxProps & { disabled?: boolean }>(({ disabled }) => ({
  cursor: disabled ? 'auto' : 'pointer',
  display: 'flex',
  width: '100%',
}))

export default ActionPreview

export type { Props as ActionPreviewProps }
